import {Button, Grid, Typography} from '@mui/material';
import React from 'react';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  ReportPackGenericFormType,
  reportPackFormType,
  ReportPackGenericFormInnerType,
  reportPackManagerCompanyNameField,
  reportPackManagerAddress1Field,
  reportPackManagerAddress2Field,
  reportPackManagerCityField,
  reportPackManagerPostcodeField,
  reportPackSIPPNumberField,
  reportPackSippTransferApproximateValueField,
  reportPackSippTransferContinueIncomeField,
  reportPackSippTransferDisqualifyingCreditsField,
  reportPackSippTransferFlexiAccessConvertField,
  reportPackSippTransferFullValueField,
  reportPackSippTransferPlanRefField,
  reportPackSippTransferSchemeTypeField,
  reportPackSippTransferDrawdownTypeField,
  reportPackSippTransferAssetsInSpecieField,
} from './ReportPackFields';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';

import {useReportPack} from './useReportPack';
import {getNumberOrNull} from '../../utils/getNumberOrNull';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';

export const ReportPackSIPPTransfer: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {onUpsertSippTransferForm} = useReportPack();
  return (
    <ReportPackSIPPTransferFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertSippTransferForm({
          variables: {
            contactCuroId: values.contactCuroId,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
            managerAddress1: values.managerAddress1,
            managerAddress2: values.managerAddress2,
            managerCity: values.managerCity,
            managerCompanyName: values.managerCompanyName,
            managerPostcode: values.managerPostcode,
            sippNumber: values.sippNumber,
            sippTransferApproximateValue: getNumberOrNull(
              values.sippTransferApproximateValue
            ),
            sippTransferContinueIncome: values.sippTransferContinueIncome,
            sippTransferDisqualifyingCredits: values.sippTransferDisqualifyingCredits,
            sippTransferDrawdownType: values.sippTransferDrawdownType,
            sippTransferFlexiAccessConvert: values.sippTransferFlexiAccessConvert,
            sippTransferFullValue: values.sippTransferFullValue,
            sippTransferPlanRef: values.sippTransferPlanRef,
            sippTransferSchemeType: values.sippTransferSchemeType,
            sippTransferAssetsInSpecie: values.sippTransferAssetsInSpecie,
          },
        })
          .then(({data}) => {
            if (data?.upsertSippTransferForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertSippTransferForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackSIPPTransferForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values} = useAutoForm<ReportPackFormFragment>();

  return (
    <Box maxWidth={500}>
      <Typography>Current provider details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerCompanyNameField.id
            )}
            field={reportPackManagerCompanyNameField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerAddress1Field.id
            )}
            field={reportPackManagerAddress1Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerAddress1Field.id
            )}
            field={reportPackManagerAddress2Field}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackManagerCityField.id)}
            field={reportPackManagerCityField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackManagerPostcodeField.id
            )}
            field={reportPackManagerPostcodeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferPlanRefField.id
            )}
            field={reportPackSippTransferPlanRefField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferAssetsInSpecieField.id
            )}
            field={reportPackSippTransferAssetsInSpecieField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferApproximateValueField.id
            )}
            field={reportPackSippTransferApproximateValueField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferSchemeTypeField.id
            )}
            field={reportPackSippTransferSchemeTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferDrawdownTypeField.id
            )}
            field={reportPackSippTransferDrawdownTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferContinueIncomeField.id
            )}
            field={reportPackSippTransferContinueIncomeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferDisqualifyingCreditsField.id
            )}
            field={reportPackSippTransferDisqualifyingCreditsField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferFlexiAccessConvertField.id
            )}
            field={reportPackSippTransferFlexiAccessConvertField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackSippTransferFullValueField.id
            )}
            field={reportPackSippTransferFullValueField}
          />
        </Grid>
      </Grid>
      <Typography>SIPP details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackSIPPNumberField.id)}
            field={reportPackSIPPNumberField}
          />
        </Grid>
      </Grid>
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackSIPPTransferFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackSIPPTransferForm, {}, {debugPosition: 'bottom'});
